import { type SxProps, type Theme } from "@mui/material";
import {
  NotificationPreferenceScope,
  NotificationPreferenceSource,
} from "generated-graphql/graphql";

import {
  NOTIFICATION_CHANNEL_EMAIL,
  NOTIFICATION_CHANNEL_IN_APP,
} from "./useNotificationPreferenceDraft";

/**
 * The words the page uses to explain where a switch position came from, and why
 * a switch that is on is not delivering anything.
 *
 * Collected here because both are the same kind of statement — an explanation of
 * state the reader did not create — and because the wording changes with the tab.
 * "Organization default" is what an inherited value is to a member; on the
 * organization's own tab that same row *is* the organization's setting, and
 * calling it a default there would suggest something above it that does not
 * exist.
 */

/** Why a staff member's switch delivers nothing while its alert is paused on All staff. */
export const PAUSED_FOR_ALL_STAFF_COPY = "Paused for all staff";

/**
 * Which layer put a switch where it is. A caption or tooltip rather than a
 * visual treatment: a reader who cannot tell their own mute from their
 * organization's will try to "fix" a setting they never made.
 */
export function provenanceLabel(
  source: NotificationPreferenceSource,
  scope: NotificationPreferenceScope
): string {
  switch (source) {
    case NotificationPreferenceSource.User:
      return "Your setting";
    case NotificationPreferenceSource.Everywhere:
      return "Your default";
    case NotificationPreferenceSource.Tenant:
      return scope === NotificationPreferenceScope.OrgDefault
        ? "Organization setting"
        : "Organization default";
    case NotificationPreferenceSource.Default:
      return "Encamp default";
  }
}

/** How the page names a channel — in prose, and as a column heading. */
export function channelLabel(channel: string): string {
  if (channel === NOTIFICATION_CHANNEL_EMAIL) {
    return "Email";
  }
  if (channel === NOTIFICATION_CHANNEL_IN_APP) {
    return "In-app";
  }
  return channel;
}

/**
 * NOTE ON THE PARENT-SWITCH COPY AND TREATMENTS BELOW
 *
 * These belong to the hierarchical design and are live: a channel or section switch
 * holds a position of its own and silences everything beneath it *without* changing
 * it, so the page has to explain a switch that is on but delivering nothing. Two cues
 * say so: the greyscale treatment, and the "these choices apply when it's turned back
 * on" copy announced as the switch's own description. A third — a badge in the column
 * header — was removed as noise; it restated the tooltip once per column.
 *
 * This machinery briefly went unused: the switches were changed to set-all-on /
 * set-all-off actions, which write every row beneath them instead of holding a
 * position, and nothing in the UI wrote a parent row. It was kept rather than
 * deleted because that call was not settled. It was then reversed, and this is the
 * behaviour that shipped — so the note that used to say "delete once final" now says
 * the opposite: this is the design, and the set-all helpers are the ones that went.
 */

/**
 * Why a switch that is on is delivering nothing.
 *
 * The promise this copy has to keep is that muting silences without clearing: the
 * switches stay where they were put and come back to life untouched. So it names
 * the switch that is actually responsible and says the choices still apply, which
 * is what stops a reader from re-setting a whole section they never lost.
 */
export function mutedByChannelParentCopy(channel: string): string {
  // Names neither "parent" nor the card it lives on: the parent switch has moved
  // once already, and copy that points at a location goes stale the next time it
  // moves. What is true regardless is that the channel is off for everything.
  return `${channelLabel(channel)} is turned off for all notifications. These choices apply when it's turned back on.`;
}

/** The same explanation when a section parent rather than a channel is the cause. */
export const MUTED_BY_SECTION_PARENT_COPY =
  "This section is turned off. These choices apply when it's turned back on.";

/**
 * What a section header says when nothing in it will reach the reader.
 *
 * Shown only in that case, and there is deliberately no counterpart for a section
 * that *is* delivering. A count ("3 on") could not say which channel it meant: it
 * folded in-app and email into one number, so a reader who muted in-app read "1 on"
 * on the one section that can still email and reasonably called it a bug. Splitting
 * it per channel needed either two numbers or a second line of header.
 *
 * The number was never the valuable half. On an expanded section every switch is
 * already visible, and on a collapsed one the fact worth surfacing is the exceptional
 * one — that this whole section is silent. Saying only that is unambiguous across both
 * channels, costs no header height, and leaves the common case quiet.
 */
export const SECTION_OFF_LABEL = "Off";

/** A section's roll-up line, named like the page-wide row above it on My preferences. */
export function rollupLabel(sectionLabel: string): string {
  return `All ${sectionLabel} notifications`;
}

/** Spells out that a roll-up moves the alerts below it, where a channel switch only silences them. */
export const ROLLUP_DESCRIPTION = "Turns every notification below on or off.";

/**
 * How a switch that is silenced from above is marked.
 *
 * Deliberately *not* an opacity on the switch. These controls are not disabled —
 * they are the primary interactive surface while a parent is off, which is the
 * state this whole page exists to express — so they have to clear WCAG 1.4.11's
 * 3:1 minimum for a user-interface component. `primary.main` alpha-composited at
 * 45% lands near 2.2:1 on white and 2.3:1 on the dark surface: both fail.
 *
 * Greyscale alone. It removes the brand colour, which is what reads as "not live",
 * while leaving the thumb's luminance almost untouched — around 7.5:1 in light and
 * 3.7:1 in dark — so the control stays visible at every step of the way it is meant
 * to be used. There is no cell tint behind it: a filled box per muted cell turned a
 * quiet state into the loudest thing on the page.
 *
 * The cue that survives for a reader who perceives no colour is the explanation
 * {@link PreferenceSwitch} announces as each switch's description — not a visual
 * treatment, and not a header badge, which restated the same fact once per column.
 */
export const MUTED_CELL_SX = {
  filter: "grayscale(1)",
};

/**
 * How a widget's own heading is set: the manifest drawer's treatment, which uses
 * a different face rather than a bigger one to mark a section boundary.
 *
 * Sized up from the bare `overline` default. At 0.75rem the label read as a
 * caption for the row beneath it rather than as the title of the widget it sits
 * in, which is most of why the page looked like one undifferentiated slab.
 */
export const SECTION_HEADING_SX: SxProps<Theme> = {
  display: "block",
  fontSize: "0.9375rem",
  lineHeight: 1.4,
  fontWeight: 700,
  letterSpacing: "0.06em",
};

/**
 * The white widget the page's sections sit in, on the gray canvas.
 *
 * Declared once and shared rather than set per section, because two surfaces that
 * are meant to read as the same kind of thing drift the moment their padding is
 * written twice.
 */
export const WIDGET_SX: SxProps<Theme> = {
  p: 2.5,
  borderRadius: 2,
  backgroundColor: "background.paper",
  border: 1,
  borderColor: "divider",
} as const;

/**
 * How every switch cell is laid out, in the header row and in all three kinds of
 * switch row.
 *
 * Centred rather than left-aligned, because the page mixes switch sizes: the
 * per-notification switches are small and the parent switches above them are
 * standard. Left-aligned, two different widths start at the same x and their
 * tracks visibly disagree; centred in a fixed-width column, they stay concentric
 * whatever size they are.
 */
export const CHANNEL_CELL_SX = {
  display: "flex",
  justifyContent: "center",
} as const;

export const UNMUTE_CONFIRM_HEADER = "Turn these alerts on?";
/*
  Muting an alert never stopped its condition being recorded — the notification was
  written and simply reached nobody — so switching it back on hands over everything
  still open.
*/
export const UNMUTE_CONFIRM_BODY: Record<NotificationPreferenceScope, string> = {
  [NotificationPreferenceScope.My]:
    "Some of these cover problems at your facilities that are still open. There could be a lot of notifications at once. Are you sure you want to turn these alerts on?",
  [NotificationPreferenceScope.OrgDefault]:
    "Some of these cover problems that are still open. There could be a lot of notifications at once. Are you sure you want to turn these alerts on?",
};
